import { keccak256, encodePacked, encodeAbiParameters, parseAbiParameters, type Hex } from 'viem';
import { getSigningKey } from './passkey';

export interface Signature {
  v: number;
  r: Hex;
  s: Hex;
}

function parseSignature(signature: Hex): Signature {
  const r = `0x${signature.slice(2, 66)}` as Hex;
  const s = `0x${signature.slice(66, 130)}` as Hex;
  const v = parseInt(signature.slice(130, 132), 16);
  return { v, r, s };
}

/**
 * Sign a recordSession message
 * Browser will prompt for passkey selection
 */
export async function signRecordSession(
  cohortId: Hex,
  sessionNonce: bigint,
  importance: number,
  participantIds: Hex[],
  points: bigint[]
): Promise<Signature> {
  const { account } = await getSigningKey();

  const innerHash = keccak256(
    encodeAbiParameters(
      parseAbiParameters('bytes32, uint256, uint8, bytes32[], uint256[]'),
      [cohortId, sessionNonce, importance, participantIds, points]
    )
  );

  const signature = await account.signMessage({
    message: { raw: innerHash }
  });

  return parseSignature(signature);
}

/**
 * Sign an addSigner message
 * Note: Requires nonce to be fetched from contract first
 */
export async function signAddSigner(
  cohortId: Hex,
  newSigner: Hex,
  nonce: bigint
): Promise<Signature> {
  const { account } = await getSigningKey();

  const innerHash = keccak256(
    encodePacked(
      ['bytes32', 'string', 'address', 'uint256'],
      [cohortId, 'addSigner', newSigner, nonce]
    )
  );

  const signature = await account.signMessage({
    message: { raw: innerHash }
  });

  return parseSignature(signature);
}

/**
 * Sign a removeSigner message
 */
export async function signRemoveSigner(
  cohortId: Hex,
  signerToRemove: Hex
): Promise<Signature> {
  const { account } = await getSigningKey();

  const innerHash = keccak256(
    encodePacked(
      ['bytes32', 'string', 'address'],
      [cohortId, 'removeSigner', signerToRemove]
    )
  );

  const signature = await account.signMessage({
    message: { raw: innerHash }
  });

  return parseSignature(signature);
}

/**
 * Sign a finalize message
 */
export async function signFinalize(
  cohortId: Hex
): Promise<Signature> {
  const { account } = await getSigningKey();

  const innerHash = keccak256(
    encodePacked(
      ['bytes32', 'string'],
      [cohortId, 'finalize']
    )
  );

  const signature = await account.signMessage({
    message: { raw: innerHash }
  });

  return parseSignature(signature);
}

/**
 * Sign an invalidateSession message
 */
export async function signInvalidateSession(
  cohortId: Hex,
  sessionId: bigint
): Promise<Signature> {
  const { account } = await getSigningKey();

  const innerHash = keccak256(
    encodePacked(
      ['bytes32', 'string', 'uint256'],
      [cohortId, 'invalidate', sessionId]
    )
  );

  const signature = await account.signMessage({
    message: { raw: innerHash }
  });

  return parseSignature(signature);
}
